import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import QuestTimeline from '../components/QuestTimeline';
import { Send, Star, Ghost } from 'lucide-react';
import { toast } from 'sonner';

function Chat({ booking, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const load = () => api.get(`/chat/${booking.booking_id}`).then(r => setMsgs(r.data)).catch(()=>{});
  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [booking.booking_id]);
  const send = async () => {
    if (!text.trim()) return;
    await api.post('/chat/send', { booking_id: booking.booking_id, message: text });
    setText(''); load();
  };
  if (!['confirmed_chat_open','completed','review_requested'].includes(booking.status)) {
    return <div className="text-sm text-[#a3a3a3] italic">Chat unlocks once your Ghostie is confirmed.</div>;
  }
  return (
    <div>
      <div className="bg-[#0a0a0a] rounded-xl p-3 h-64 overflow-y-auto space-y-2 border border-[#3f2e1e]">
        {msgs.length === 0 && <div className="text-xs text-[#a3a3a3] italic">Say hi to your Ghostie ✨</div>}
        {msgs.map(m => (
          <div key={m.message_id} className={`text-sm ${m.sender_id===me.user_id?'text-right':''}`}>
            <div className="text-[10px] text-[#a3a3a3] font-accent uppercase">{m.sender_name || m.sender_role}</div>
            <div className={`inline-block px-3 py-2 rounded-2xl ${m.sender_id===me.user_id?'bg-[#f97316] text-[#0a0a0a]':'bg-[#262626]'}`}>{m.message}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <input className="input-cozy flex-1" value={text} onChange={e=>setText(e.target.value)} placeholder="Write a message..." onKeyDown={e=>e.key==='Enter'&&send()} data-testid="chat-input"/>
        <button onClick={send} className="btn-primary" data-testid="chat-send"><Send size={14}/></button>
      </div>
    </div>
  );
}

function ReviewForm({ booking, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const submit = async () => {
    try { await api.post('/reviews', { booking_id: booking.booking_id, rating, text }); toast.success('Review sent'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="magical-card p-5">
      <h4 className="font-heading text-lg mb-3">Leave a review</h4>
      <div className="flex gap-1 mb-3">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={()=>setRating(n)} className={`p-1 ${n<=rating?'text-[#f97316]':'text-[#3f2e1e]'}`} data-testid={`star-${n}`}><Star size={22} fill="currentColor"/></button>
        ))}
      </div>
      <textarea rows={3} className="input-cozy" placeholder="How was your meetup?" value={text} onChange={e=>setText(e.target.value)} data-testid="review-text"/>
      <button onClick={submit} className="btn-primary mt-3" data-testid="review-submit">Send review</button>
    </div>
  );
}

function CancellationCard({ booking, onCancelled }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Calculate hours until start
  let hoursUntil = Infinity;
  try {
    const start = new Date(`${booking.date}T${booking.start_time}:00`);
    hoursUntil = (start.getTime() - Date.now()) / 36e5;
  } catch {}
  const eligible = ['paid_pending_match','external_paid_pending_match','host_applications_open','host_assigned_pending_confirmation','confirmed_chat_open'].includes(booking.status);
  const within72 = hoursUntil < 72;
  if (!eligible) return null;

  const cancel = async () => {
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/cancel`);
      toast.success('Booking cancelled — refund will follow');
      onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); setShowConfirm(false); }
  };

  return (
    <div className="magical-card p-5 border-red-900/30">
      <h4 className="font-heading text-lg mb-2">🚪 Cancel this booking</h4>
      {within72 ? (
        <p className="text-sm text-[#a3a3a3]">Within 72h of start time. Please contact <a href="mailto:hello@ghostie.it" className="text-[#f97316]">hello@ghostie.it</a> to discuss options.</p>
      ) : !showConfirm ? (
        <button onClick={()=>setShowConfirm(true)} className="chip" data-testid="cancel-booking-btn">Cancel booking</button>
      ) : (
        <div>
          <p className="text-sm text-[#a3a3a3] mb-3">Free cancellation. A refund will be processed within a few days.</p>
          <div className="flex gap-2">
            <button onClick={cancel} disabled={submitting} className="btn-primary text-sm py-2 px-4" data-testid="cancel-confirm-btn">{submitting?'Cancelling…':'Yes, cancel'}</button>
            <button onClick={()=>setShowConfirm(false)} className="btn-secondary text-sm py-2 px-4">Keep booking</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get('/bookings/mine').then(r => { setBookings(r.data); if (r.data[0] && !selected) setSelected(r.data[0]); });
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-3 mb-8">
        <Ghost size={28} className="text-[#f97316]"/>
        <h1 className="text-3xl font-heading font-semibold">Hi {user?.name?.split(' ')[0] || 'traveler'} — your bookings</h1>
      </div>

      <div className="magical-card p-5 mb-8 border-[#f97316]/50">
        <h2 className="font-heading text-2xl mb-2">Verify you are 18+</h2>
        <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">
          To continue booking, please verify that you are 18+. Your ID will be checked securely by an external provider and won’t be stored by Ghostie.
        </p>
        <button className="btn-secondary text-sm" type="button">Verify 18+</button>
      </div>

      {bookings.length === 0 ? (
        <div className="magical-card p-10 text-center">
          <p className="text-[#a3a3a3] mb-4">No bookings yet. Book your first one.</p>
          <a href="/book" className="btn-primary">Book Ghostie Time</a>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr_2fr] gap-6">
          <div className="space-y-3">
            {bookings.map(b => (
              <button key={b.booking_id} onClick={()=>setSelected(b)} className={`block w-full text-left magical-card p-4 ${selected?.booking_id===b.booking_id?'border-[#f97316]':''}`} data-testid={`booking-${b.booking_id}`}>
                <div className="font-heading text-lg">{b.date} · {b.start_time}</div>
                <div className="text-xs text-[#a3a3a3]">{b.duration_hours}h · {b.number_of_people} people · €{b.total_paid}</div>
                <div className="badge-cozy mt-2 text-[10px]">{b.status.replace(/_/g,' ')}</div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="space-y-5">
              <QuestTimeline status={selected.status}/>
              <div className="magical-card p-5">
                <h3 className="font-heading text-xl mb-3">Chat with your Ghostie</h3>
                {user && <Chat booking={selected} me={user}/>}
              </div>
              {['completed','review_requested'].includes(selected.status) && (
                <ReviewForm booking={selected} onDone={load}/>
              )}
              <CancellationCard booking={selected} onCancelled={load}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
